import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const AGENCY_URL = 'https://wiselydrivenai.com';

const styles = {
  page: {
    backgroundColor: '#111827',
    minHeight: '100vh',
    fontFamily: "'Outfit', system-ui, sans-serif",
    color: '#F9FAFB',
    padding: '0 0 4rem',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 2rem',
    borderBottom: '1px solid #1F2937',
    backgroundColor: 'rgba(17,24,39,0.95)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  logoText: {
    fontFamily: "'DM Serif Display', Georgia, serif",
    fontSize: '1.25rem',
    color: '#F9FAFB',
    textDecoration: 'none',
  },
  backBtn: {
    background: 'none',
    border: '1px solid #374151',
    borderRadius: '9999px',
    color: '#9CA3AF',
    padding: '0.4rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontFamily: "'Outfit', system-ui, sans-serif",
  },
  container: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '2.5rem 1.5rem',
  },
  h1: {
    fontFamily: "'DM Serif Display', Georgia, serif",
    fontSize: '2rem',
    marginBottom: '0.5rem',
    color: '#F9FAFB',
  },
  sub: {
    color: '#9CA3AF',
    marginBottom: '2.5rem',
    lineHeight: 1.6,
  },
  section: {
    marginBottom: '2rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#F97316',
    fontWeight: '600',
    marginBottom: '1rem',
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    color: '#D1D5DB',
    marginBottom: '0.375rem',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    backgroundColor: '#1F2937',
    border: '1px solid #374151',
    borderRadius: '0.5rem',
    color: '#F9FAFB',
    padding: '0.625rem 0.875rem',
    fontSize: '0.95rem',
    fontFamily: "'Outfit', system-ui, sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    backgroundColor: '#1F2937',
    border: '1px solid #374151',
    borderRadius: '0.5rem',
    color: '#F9FAFB',
    padding: '0.625rem 0.875rem',
    fontSize: '0.95rem',
    fontFamily: "'Outfit', system-ui, sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '80px',
  },
  websiteCallout: {
    backgroundColor: 'rgba(249,115,22,0.08)',
    border: '1px solid rgba(249,115,22,0.2)',
    borderRadius: '0.5rem',
    padding: '0.75rem 1rem',
    fontSize: '0.8rem',
    color: '#FED7AA',
    marginTop: '0.5rem',
  },
  consentBox: {
    backgroundColor: '#1F2937',
    border: '1px solid #374151',
    borderRadius: '0.75rem',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  consentLabel: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
    cursor: 'pointer',
    color: '#D1D5DB',
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  checkbox: {
    marginTop: '0.2rem',
    width: '1rem',
    height: '1rem',
    cursor: 'pointer',
    flexShrink: 0,
    accentColor: '#F97316',
  },
  submitBtn: {
    width: '100%',
    backgroundColor: '#F97316',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '9999px',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Outfit', system-ui, sans-serif",
    transition: 'background-color 0.2s',
  },
  error: {
    backgroundColor: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '0.5rem',
    padding: '0.875rem 1rem',
    color: '#FCA5A5',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
};

const SOCIAL_FIELDS = [
  { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/in/yourname' },
  { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/yourpage' },
  { key: 'twitter', label: 'X / Twitter URL', placeholder: 'https://x.com/yourhandle' },
];

export default function GetMyCard() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '', lastName: '', jobTitle: '', company: '',
    email: '', phone: '', websiteUrl: '',
    linkedin: '', instagram: '', facebook: '', twitter: '',
    avatar: '', message: '', consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.consent) {
      setError('Please check the consent box to create your card.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/public/create-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, consent: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      window.location.href = data.cardUrl;
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputFocus = (e) => { e.target.style.borderColor = '#F97316'; };
  const inputBlur = (e) => { e.target.style.borderColor = '#374151'; };

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <a href={AGENCY_URL} style={styles.logoText} target="_blank" rel="noopener noreferrer">
          Wisely Driven <span style={{ color: '#F97316' }}>Consulting</span>
        </a>
        <button style={styles.backBtn} onClick={() => navigate('/')}>
          ← Back
        </button>
      </nav>

      <div style={styles.container}>
        <h1 style={styles.h1}>Create your free card</h1>
        <p style={styles.sub}>
          Fill in your details below. Your digital card and QR code will be ready instantly.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Your Info</div>
            <div style={styles.grid2}>
              <div style={styles.field}>
                <label style={styles.label}>First name *</label>
                <input style={styles.input} value={form.firstName} onChange={set('firstName')}
                  onFocus={inputFocus} onBlur={inputBlur} required maxLength={100} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Last name *</label>
                <input style={styles.input} value={form.lastName} onChange={set('lastName')}
                  onFocus={inputFocus} onBlur={inputBlur} required maxLength={100} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Job title *</label>
              <input style={styles.input} value={form.jobTitle} onChange={set('jobTitle')}
                onFocus={inputFocus} onBlur={inputBlur} required maxLength={200} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Company *</label>
              <input style={styles.input} value={form.company} onChange={set('company')}
                onFocus={inputFocus} onBlur={inputBlur} required maxLength={200} />
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionLabel}>Contact Details</div>
            <div style={styles.field}>
              <label style={styles.label}>Email *</label>
              <input style={styles.input} type="email" value={form.email} onChange={set('email')}
                onFocus={inputFocus} onBlur={inputBlur} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone *</label>
              <PhoneInput
                international
                defaultCountry="CA"
                value={form.phone}
                onChange={set('phone')}
                style={{ '--PhoneInputCountryFlag-height': '1em' }}
                inputStyle={{ ...styles.input, width: '100%' }}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Website URL</label>
              <input style={styles.input} type="url" value={form.websiteUrl} onChange={set('websiteUrl')}
                onFocus={inputFocus} onBlur={inputBlur}
                placeholder="https://yourbusiness.com" />
              {form.websiteUrl && (
                <div style={styles.websiteCallout}>
                  We'll try to match your card colors to your brand. Best effort — not guaranteed.
                </div>
              )}
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionLabel}>Social Links <span style={{ color: '#6B7280', textTransform: 'none', fontWeight: 400 }}>(optional)</span></div>
            {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
              <div key={key} style={styles.field}>
                <label style={styles.label}>{label}</label>
                <input style={styles.input} type="url" value={form[key]} onChange={set(key)}
                  onFocus={inputFocus} onBlur={inputBlur} placeholder={placeholder} />
              </div>
            ))}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionLabel}>Profile Photo <span style={{ color: '#6B7280', textTransform: 'none', fontWeight: 400 }}>(optional)</span></div>
            <div style={styles.field}>
              <label style={styles.label}>Upload your photo</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ ...styles.input, padding: '0.5rem', cursor: 'pointer' }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const data = new FormData();
                  data.append('file', file);
                  try {
                    const res = await fetch('/api/public/upload', { method: 'POST', body: data });
                    if (res.ok) {
                      const json = await res.json();
                      setForm(f => ({ ...f, avatar: json.url || json.path || '' }));
                    }
                  } catch {}
                }}
              />
              {form.avatar && (
                <div style={{ marginTop: '0.5rem' }}>
                  <img src={form.avatar} alt="avatar preview" style={{ width: '4rem', height: '4rem', borderRadius: '50%', objectFit: 'cover', border: '2px solid #F97316' }} />
                </div>
              )}
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionLabel}>Anything else? <span style={{ color: '#6B7280', textTransform: 'none', fontWeight: 400 }}>(optional)</span></div>
            <textarea
              style={styles.textarea}
              value={form.message}
              onChange={set('message')}
              placeholder="Any additional info you'd like on your card..."
              maxLength={1000}
              onFocus={(e) => { e.target.style.borderColor = '#F97316'; }}
              onBlur={(e) => { e.target.style.borderColor = '#374151'; }}
            />
          </div>

          <div style={styles.consentBox}>
            <label style={styles.consentLabel}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={form.consent}
                onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                required
              />
              <span>
                I agree that <strong>Wisely Driven Consulting</strong> may contact me in the future
                about their digital services. I understand this is in exchange for my free digital business card.
              </span>
            </label>
          </div>

          <button
            type="submit"
            style={{
              ...styles.submitBtn,
              backgroundColor: loading ? '#9A3412' : '#F97316',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
            onMouseEnter={e => { if (!loading) e.target.style.backgroundColor = '#EA580C'; }}
            onMouseLeave={e => { if (!loading) e.target.style.backgroundColor = '#F97316'; }}
          >
            {loading ? 'Creating your card...' : 'Create My Free Card →'}
          </button>

          <p style={{ textAlign: 'center', color: '#6B7280', fontSize: '0.8rem', marginTop: '1.25rem' }}>
            Powered by{' '}
            <a href={AGENCY_URL} style={{ color: '#F97316', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
              Wisely Driven Consulting
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
